import React, { useEffect, useState, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Button,
  Chip,
  Alert,
  TextField,
  InputAdornment,
} from "@mui/material";
import { X as CloseIcon, Eye as ViewIcon, Calendar as CalendarIcon, Search as SearchIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "../../../helpers/Endpoints/endpoints";
import { MecordColors } from "Constants/Color";
import MasterTableContainer from "../../TagsSearchPage/MasterFiltersTable/MasterTableContainer";
import EnquiryViewComponent from "../../Enquiry/Components/EnquiryViewComponent";
import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { mergeDashboardFilters, DashboardColFilter, scopedDropdownProps } from "./dashboardSidebarFilters";

interface DashboardEnquirySidebarProps {
  open: boolean;
  onClose: () => void;
  type: "product" | "service";
  dateRange: {
    from_date: string;
    to_date: string;
  } | null;
  kpiId?: string;
}

export const DashboardEnquirySidebar: React.FC<DashboardEnquirySidebarProps> = ({
  open,
  onClose,
  type,
  dateRange,
  kpiId = "new_enquiry",
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [filterValue, setFilterValue] = useState<DashboardColFilter[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Local state for inline enquiry detail viewing
  const [viewingEnquiryId, setViewingEnquiryId] = useState<string | null>(null);
  const [viewingEnquiryData, setViewingEnquiryData] = useState<any | null>(null);
  const [viewingLoading, setViewingLoading] = useState(false);
  const [viewingError, setViewingError] = useState<string | null>(null);

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  const fetchEnquiries = async () => {
    if (!dateRange) return;

    setLoading(true);
    try {
      const url =
        type === "product"
          ? API_ENDPOINT.ENQUIRY.PRODUCT.LIST
          : API_ENDPOINT.ENQUIRY.SERVICE.LIST;

      const baseFilters: any = {
        enquiry_stage: "enquiry_log",
        range: {
          enquiry_date: {
            from: dateRange.from_date,
            to: dateRange.to_date,
          },
        },
      };

      if (kpiId === "open_enquiry" || kpiId === "open_service_enquiries") {
        baseFilters.enquiry_status = "In Progress";
      }

      // Same FilterBuilder merge as original enquiry listing thunk
      const filters = mergeDashboardFilters(baseFilters, filterValue);

      // Keep KPI lock so column filters can't escape the card's meaning
      if (kpiId === "open_enquiry" || kpiId === "open_service_enquiries") {
        filters.enquiry_status = "In Progress";
      }
      filters.enquiry_stage = filters.enquiry_stage || "enquiry_log";

      // Same query params as fetchProductEnquiries / fetchServiceEnquiries
      const params = new URLSearchParams();
      params.append("page", String(page + 1));
      params.append("page_size", String(rowsPerPage));
      if (search) params.append("search", search);
      if (sortField) {
        params.append(
          "ordering",
          sortDirection === "desc" ? `-${sortField}` : sortField
        );
      }

      const response = await Axios.post(`${url}?${params.toString()}`, {
        filters,
      });

      setData(response.data?.data || []);
      setTotalCount(response.data?.totalRecords || 0);
    } catch (error) {
      console.error("Failed to fetch enquiries inside dashboard sidebar:", error);
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search (same as enquiry listing)
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    if (open && dateRange) {
      fetchEnquiries();
    }
  }, [
    open,
    type,
    dateRange,
    page,
    rowsPerPage,
    kpiId,
    search,
    filterValue,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    if (open) {
      setPage(0);
      setSearchInput("");
      setSearch("");
      setSorting([]);
      setColumnFilters([]);
      setFilterValue([]);
    }
  }, [open, type, dateRange, kpiId]);

  useEffect(() => {
    if (open) setPage(0);
  }, [filterValue, sortField, sortDirection]);

  // Fetch single enquiry details when viewingEnquiryId is set
  useEffect(() => {
    const fetchDetail = async () => {
      if (!viewingEnquiryId) return;

      setViewingLoading(true);
      setViewingError(null);
      setViewingEnquiryData(null);
      try {
        const url =
          type === "product"
            ? API_ENDPOINT.ENQUIRY.PRODUCT.DETAIL(viewingEnquiryId)
            : API_ENDPOINT.ENQUIRY.SERVICE.DETAIL(viewingEnquiryId);

        const res = await Axios.get(url);
        setViewingEnquiryData(res.data?.data);
      } catch (err: any) {
        console.error("Failed to fetch enquiry detail:", err);
        setViewingError(err?.message || "Failed to fetch enquiry details.");
      } finally {
        setViewingLoading(false);
      }
    };
    fetchDetail();
  }, [viewingEnquiryId, type]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (e) {
      return dateStr;
    }
  };

  const getStatusColor = (status: string) => {
    const s = (status || "").toLowerCase();
    if (s.includes("converted")) return "success";
    if (s.includes("lost") || s.includes("junk")) return "error";
    if (s.includes("progress")) return "warning";
    return "default";
  };

  const themeColor = MecordColors?.ThemeBGColor || "#d2363a";
  const headerBgColor = MecordColors?.LightThemeBGColor || "#f8e8e8";

  const kpiBaseFilters = useMemo(() => {
    const base: any = {
      enquiry_stage: "enquiry_log",
      range: dateRange
        ? {
            enquiry_date: {
              from: dateRange.from_date,
              to: dateRange.to_date,
            },
          }
        : undefined,
    };
    if (kpiId === "open_enquiry" || kpiId === "open_service_enquiries") {
      base.enquiry_status = "In Progress";
    }
    return base;
  }, [dateRange, kpiId]);

  const columns = useMemo(() => {
    const source = type === "product" ? "enquiry_product" : "enquiry_service";

    return [
      {
        header: "Enquiry No.",
        accessorKey: "enquiry_number",
        enableSorting: true,
        ...scopedDropdownProps(source, "enquiry_number", kpiBaseFilters),
        cell: ({ row }: { row: any }) => (
          <span style={{ fontWeight: 600, color: themeColor }}>
            {row.original.enquiry_number || "-"}
          </span>
        ),
      },
      {
        header: "Date",
        accessorKey: "enquiry_date",
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => formatDate(row.original.enquiry_date),
      },
      {
        header: "Customer Name",
        accessorKey: "customer_name",
        enableSorting: true,
        ...scopedDropdownProps(source, "customer_name", kpiBaseFilters),
      },
      {
        header: "Status",
        accessorKey: "enquiry_status",
        enableSorting: true,
        ...scopedDropdownProps(source, "enquiry_status", kpiBaseFilters),
        cell: ({ row }: { row: any }) => (
          <Chip
            label={row.original.enquiry_status || "N/A"}
            color={getStatusColor(row.original.enquiry_status)}
            size="small"
            sx={{
              fontSize: "10px",
              height: 20,
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500,
              borderRadius: "4px",
            }}
          />
        ),
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const enquiryId =
            type === "product"
              ? row.original.enquiry_product_id
              : row.original.enquiry_service_id;
          return (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ViewIcon size={12} />}
              onClick={() => {
                setViewingEnquiryId(enquiryId);
              }}
              sx={{
                fontSize: "11px",
                py: 0.25,
                px: 1.25,
                fontFamily: "'Poppins', sans-serif",
                textTransform: "none",
                borderColor: themeColor,
                color: themeColor,
                borderRadius: "6px",
                fontWeight: 500,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: themeColor,
                  backgroundColor: headerBgColor,
                  transform: "scale(1.02)",
                },
              }}
            >
              View
            </Button>
          );
        },
      },
    ];
  }, [type, themeColor, headerBgColor, kpiBaseFilters]);

  return (
    <>
      {/* 1. Main Drawer: Enquiry Listings */}
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(4px)",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
            },
          },
        }}
        PaperProps={{
          sx: {
            width: "100%",
            "@media (min-width: 768px)": {
              width: "calc(100vw - var(--tb-vertical-menu-width, 250px))",
            },
            p: 3,
            background: "linear-gradient(135deg, #ffffff 0%, #fefefe 50%, #f8f9fa 100%)",
            borderTopLeftRadius: "20px",
            borderBottomLeftRadius: "20px",
            borderLeft: "1px solid rgba(0, 0, 0, 0.08)",
            boxShadow: "-16px 0 48px rgba(0, 0, 0, 0.16), -4px 0 12px rgba(0, 0, 0, 0.08), inset 1px 0 0 rgba(255, 255, 255, 0.8)",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, pb: 1.5, boxSizing: "border-box", fontFamily: "'Poppins', sans-serif" }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pb: 2,
              borderBottom: "1px solid #e5e7eb",
              mb: 2.5,
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontFamily: "'Poppins', sans-serif",
                    color: "#111827",
                    fontSize: "18px",
                  }}
                >
                  {kpiId === "open_enquiry" || kpiId === "open_service_enquiries" ? "Open Enquiries" : "New Enquiries"}
                </Typography>
                <Chip
                  label={type === "product" ? "Sales" : "Service"}
                  size="small"
                  sx={{
                    backgroundColor: headerBgColor,
                    color: themeColor,
                    fontWeight: 600,
                    fontSize: "11px",
                    height: 20,
                    borderRadius: "6px",
                    fontFamily: "'Poppins', sans-serif"
                  }}
                />
              </Box>
              {dateRange && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#6b7280" }}>
                  <CalendarIcon size={12} />
                  <Typography sx={{ fontSize: "11px", fontFamily: "'Poppins', sans-serif" }}>
                    Period: {formatDate(dateRange.from_date)} to {formatDate(dateRange.to_date)}
                  </Typography>
                </Box>
              )}
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: "#9ca3af" }}>
              <CloseIcon size={20} />
            </IconButton>
          </Box>

          {/* Search bar */}
          <Box sx={{ mb: 2.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by enquiry number, customer name, email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon size={18} style={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchInput("")}
                      edge="end"
                      sx={{ color: "#9ca3af" }}
                    >
                      <CloseIcon size={16} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "#f9fafb",
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "13px",
                  "& fieldset": {
                    borderColor: "#e5e7eb",
                  },
                  "&:hover fieldset": {
                    borderColor: "#d1d5db",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: themeColor,
                  },
                },
              }}
            />
          </Box>

          {/* Listing Table Area */}
          <Box
            className="sidebar-table-scroll-container"
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              backgroundColor: "#ffffff",
              boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.02)",
              position: "relative"
            }}
          >
            <MasterTableContainer
              columns={columns}
              data={data}
              totalRecords={totalCount}
              pageIndex={page}
              pageSize={rowsPerPage}
              onPageChange={setPage}
              onPageSizeChange={setRowsPerPage}
              sorting={sorting}
              onSortingChange={setSorting}
              manualSorting={true}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              colValue={setFilterValue}
            />
          </Box>
        </Box>
      </Drawer>

      {/* 2. Nested Drawer: View Single Enquiry Details Inline */}
      <Drawer
        anchor="right"
        open={!!viewingEnquiryId}
        onClose={() => setViewingEnquiryId(null)}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(4px)",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
            },
          },
        }}
        PaperProps={{
          sx: {
            width: "100%",
            "@media (min-width: 768px)": {
              width: "calc(100vw - var(--tb-vertical-menu-width, 250px))",
            },
            p: 3,
            background: "linear-gradient(135deg, #ffffff 0%, #fefefe 50%, #f8f9fa 100%)",
            borderTopLeftRadius: "20px",
            borderBottomLeftRadius: "20px",
            borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
            boxShadow: "-24px 0 54px rgba(0, 0, 0, 0.22), -6px 0 16px rgba(0, 0, 0, 0.1)",
            zIndex: 1400, // Positioned layered on top of the first list drawer
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, pb: 1.5, boxSizing: "border-box", fontFamily: "'Poppins', sans-serif" }}>
          {/* Details Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pb: 2,
              borderBottom: "1px solid #e5e7eb",
              mb: 2.5,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontFamily: "'Poppins', sans-serif",
                  color: "#111827",
                  fontSize: "18px",
                }}
              >
                Enquiry Details
              </Typography>
              {viewingEnquiryData && (
                <Typography sx={{ fontSize: "12px", color: "#6b7280", fontFamily: "'Poppins', sans-serif" }}>
                  No: {viewingEnquiryData.enquiry_number}
                </Typography>
              )}
            </Box>
            <IconButton onClick={() => setViewingEnquiryId(null)} size="small" sx={{ color: "#9ca3af" }}>
              <CloseIcon size={20} />
            </IconButton>
          </Box>

          {/* Details View Body (Limited layout heights for inner scrolls) */}
          <Box
            className="view-drawer-content"
            sx={{
              flex: 1,
              overflowY: "auto",
              pr: 0.5,
              position: "relative",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "#cbd5e1", borderRadius: "3px" },
              /* Condensed child card styling styles override */
              "& .card": {
                boxShadow: "none !important",
                border: "none !important",
                mb: "0px !important",
                backgroundColor: "transparent !important",
              },
              "& .card-body": {
                p: "0px !important",
              },
              "& hr": {
                my: "12px !important",
                borderColor: "#e5e7eb !important",
              },
              "& .section": {
                mb: "12px !important",
              },
              "& p, & td, & th": {
                fontSize: "13px !important",
                lineHeight: "1.4 !important",
              },
              "& p": {
                mb: "6px !important",
              },
              "& .table th, & .table td": {
                p: "8px 10px !important",
              }
            }}
          >
            {viewingLoading ? (
              <LinearProgress sx={{ mt: 2 }} />
            ) : viewingError ? (
              <Alert severity="error" sx={{ mt: 2 }}>{viewingError}</Alert>
            ) : viewingEnquiryData ? (
              <EnquiryViewComponent details={viewingEnquiryData} type={type} />
            ) : null}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
